import { KakaoAlimtalkRecipientItem, StoreDoc, UserDoc } from '@gooduncles/gu-app-schema';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Radio, Typography, notification } from 'antd';
import { chunk, keyBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import {
  ServiceVersionFilter,
  countByServiceVersion,
  formatServiceVersionGroupCount,
  matchesServiceVersionFilter,
  senderKey,
  serviceVersionFilterOptions,
} from 'src/constants';

import { errorObjectToString, sleep } from 'src/utils/common';
import { callSendKakaoAlimtalk } from 'src/utils/firebase/firebase-callable';
import { getAlimtalkMessage } from 'src/utils/firebase/firebase-short-cut';
import { organizeKakaotalkResultMessage } from 'src/utils/nhn-notification-util';

import useSWRStores from 'src/hooks/swr/useSWRStores';
import useSWRUsers from 'src/hooks/swr/useSWRUsers';

import Loading from 'src/components/Loading/Loading';

import { FallbackPhoneControl, InvalidPhoneButton } from './InvalidPhoneModal';
import classes from './KakaoAlimtalkSend.module.scss';
import { AlimtalkTemplateConfig } from './types';
import UserSelectionTable, { isValidMobileNumber, UserWithStore } from './UserSelectionTable';

const mergeUsersWithStores = (users: UserDoc[], stores: StoreDoc[]): UserWithStore[] => {
  const storeMap = keyBy(stores, '_id');
  return users
    .filter((user) => storeMap[user.storeId])
    .map((user) => {
      const store = storeMap[user.storeId];
      return {
        ...user,
        storeName: store.storeName,
        storeNickname: store.storeNickname,
        storeCode: store.storeCode,
        storeState: store.state,
        storeType: store.storeType,
        storeAddress: store.roadAddress || store.address || '',
        storeCategories: store.categories ?? [],
        ownerTel: store.ownerTel ?? '',
        latestOrderAt: store.latestOrderAt ?? null,
        businessName: store.businessName ?? '',
      };
    });
};

interface KakaoAlimtalkSendProps<T extends Record<string, string>> {
  config: AlimtalkTemplateConfig<T>;
}

function KakaoAlimtalkSend<T extends Record<string, string>>({ config }: KakaoAlimtalkSendProps<T>) {
  const { templateCode, isComplete, FormComponent, PreviewComponent } = config;

  const [templateParams, setTemplateParams] = useState<Partial<T>>({});
  const [selectedUsers, setSelectedUsers] = useState<UserWithStore[]>([]);
  const [useOwnerTelFallback, setUseOwnerTelFallback] = useState(false);
  const [storeTypeFilter, setStoreTypeFilter] = useState<'all' | 'normal' | 'staff'>('all');
  const [serviceVersionFilter, setServiceVersionFilter] = useState<ServiceVersionFilter>('all');
  const [loading, setLoading] = useState(false);

  const { users, usersLoading, refresh: refreshUsers } = useSWRUsers();
  const { stores, isLoading: storesLoading, refresh: refreshStores } = useSWRStores([['state', '!=', 'closed']]);

  const userWithStores = useMemo(() => {
    if (!users || !stores) return [];
    const activeUsers = users.filter((user) => user.status === 'active');
    return mergeUsersWithStores(activeUsers, stores);
  }, [users, stores]);

  const filteredUsers = useMemo(
    () =>
      userWithStores
        .filter((user) => storeTypeFilter === 'all' || user.storeType === storeTypeFilter)
        .filter((user) => matchesServiceVersionFilter(user.serviceVersion, serviceVersionFilter)),
    [userWithStores, storeTypeFilter, serviceVersionFilter],
  );

  const substitutableUsers = useMemo(
    () =>
      filteredUsers
        .filter((user) => !isValidMobileNumber(user.userTel ?? ''))
        .filter((user) => isValidMobileNumber(user.ownerTel)),
    [filteredUsers],
  );

  const filteredVersionCounts = useMemo(() => countByServiceVersion(filteredUsers), [filteredUsers]);
  const selectedVersionCounts = useMemo(() => countByServiceVersion(selectedUsers), [selectedUsers]);

  const dataLoading = usersLoading || storesLoading;

  const handleRefresh = useCallback(() => {
    refreshUsers();
    refreshStores();
    setSelectedUsers([]);
    notification.info({ message: '사용자 및 매장 목록을 갱신합니다.' });
  }, [refreshUsers, refreshStores]);

  const handleSend = useCallback(async () => {
    if (!senderKey) {
      notification.error({ message: 'senderKey가 설정되지 않았습니다.' });
      return;
    }

    if (!isComplete(templateParams)) {
      notification.warning({ message: '모든 템플릿 항목을 입력해주세요.' });
      return;
    }

    const fallbackUsers = useOwnerTelFallback ? substitutableUsers : [];
    const totalCount = selectedUsers.length + fallbackUsers.length;
    if (totalCount === 0) {
      notification.warning({ message: '발송 대상을 선택해주세요.' });
      return;
    }

    const toRecipient = (tel: string, storeId: string): KakaoAlimtalkRecipientItem<T> => ({
      recipientNo: tel.replace(/\D/g, ''),
      templateParameter: templateParams as T,
      recipientGroupingKey: storeId,
    });

    const recipientList = [
      ...selectedUsers
        .filter((user) => isValidMobileNumber(user.userTel))
        .map((user) => toRecipient(user.userTel, user.storeId)),
      ...fallbackUsers.map((user) => toRecipient(user.ownerTel, user.storeId)),
    ];

    if (recipientList.length === 0) {
      notification.warning({ message: '유효한 연락처를 가진 사용자가 없습니다.' });
      return;
    }

    setLoading(true);
    try {
      const chunkedList = chunk(recipientList, 20);
      for (const chunkedItem of chunkedList) {
        const result = await callSendKakaoAlimtalk({
          senderKey,
          recipientList: chunkedItem,
          templateCode,
        });

        if (result.data.result !== 'success') {
          throw new Error(`카카오톡 전송 요청에 실패했습니다. ${result.data.reason ?? '알 수 없는 오류'}`);
        }

        if (result.data.alimtalkMessageDocId) {
          const alimtalkMessageDoc = await getAlimtalkMessage<T>(result.data.alimtalkMessageDocId);
          if (alimtalkMessageDoc) {
            const description = organizeKakaotalkResultMessage(alimtalkMessageDoc.recipientWithResultList);
            notification.success({
              message: '전송 요청 성공',
              description,
              className: 'pre-line-notification',
            });
          }
        }

        await sleep(300);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = errorObjectToString(error);
      notification.error({
        message: '알림톡 발송에 실패했습니다.',
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [templateParams, selectedUsers, useOwnerTelFallback, substitutableUsers, isComplete, templateCode]);

  if (dataLoading) {
    return <Loading title='사용자 및 매장 정보를 불러오는 중...' />;
  }

  const templateReady = isComplete(templateParams);
  const fallbackCount = useOwnerTelFallback ? substitutableUsers.length : 0;
  const totalSendCount = selectedUsers.length + fallbackCount;
  const sendDisabled = !templateReady || totalSendCount === 0;

  return (
    <div className={classes.container}>
      <div className={classes.topSection}>
        <div className={classes.formSection}>
          <FormComponent onValuesChange={setTemplateParams} />
        </div>
        <div className={classes.tableWrapper}>
          <div className={classes.selectionInfo}>
            <div className={classes.filterStack}>
              <Typography.Text strong>
                발송 대상 선택 (총 {filteredUsers.length}명 / 선택: {selectedUsers.length}명)
              </Typography.Text>
              <Typography.Text type='secondary'>
                대상 {formatServiceVersionGroupCount(filteredVersionCounts.v1, filteredVersionCounts.v2)} · 선택{' '}
                {formatServiceVersionGroupCount(selectedVersionCounts.v1, selectedVersionCounts.v2)}
              </Typography.Text>
            </div>
            <div className={classes.filterStack}>
              <Radio.Group
                size='small'
                value={storeTypeFilter}
                onChange={(e) => {
                  setStoreTypeFilter(e.target.value);
                  setSelectedUsers([]);
                }}
                optionType='button'
                buttonStyle='solid'
                options={[
                  { label: '전체', value: 'all' },
                  { label: '일반', value: 'normal' },
                  { label: '직원', value: 'staff' },
                ]}
              />
              <Radio.Group
                size='small'
                value={serviceVersionFilter}
                onChange={(e) => {
                  setServiceVersionFilter(e.target.value);
                  setSelectedUsers([]);
                }}
                optionType='button'
                buttonStyle='solid'
                options={serviceVersionFilterOptions}
              />
            </div>
            <div className={classes.filterStack}>
              <Button icon={<ReloadOutlined />} size='small' onClick={handleRefresh} loading={dataLoading}>
                새로고침
              </Button>
              <InvalidPhoneButton users={filteredUsers} />
            </div>
            <div className={classes.filterStack}>
              <FallbackPhoneControl
                users={filteredUsers}
                useOwnerTelFallback={useOwnerTelFallback}
                onFallbackChange={setUseOwnerTelFallback}
              />
            </div>
          </div>
          <UserSelectionTable rowData={filteredUsers} onSelectionChanged={setSelectedUsers} />
        </div>
        <div className={classes.previewSection}>
          <Typography.Text strong>미리보기</Typography.Text>
          <PreviewComponent templateParams={templateParams} />
          <div className={classes.sendArea}>
            <Typography.Text strong>최종 발송 대상: {totalSendCount}명</Typography.Text>
            <Typography.Text type='secondary'>
              {formatServiceVersionGroupCount(selectedVersionCounts.v1, selectedVersionCounts.v2)}
            </Typography.Text>
            {fallbackCount > 0 && (
              <Typography.Text type='secondary'>(점주 연락처 대체 {fallbackCount}명 포함)</Typography.Text>
            )}
            <Popconfirm
              title={`${totalSendCount}명에게 알림톡을 발송하시겠습니까?${fallbackCount > 0 ? ` (점주 연락처 대체 ${fallbackCount}명 포함)` : ''}`}
              onConfirm={handleSend}
              okText='발송'
              cancelText='취소'
              disabled={sendDisabled}
            >
              <Button type='primary' size='large' loading={loading} disabled={sendDisabled} block>
                알림톡 발송 ({totalSendCount}명)
              </Button>
            </Popconfirm>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KakaoAlimtalkSend;
